import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { match } from 'ts-pattern';
import ActionButton from '@/components/Button/ActionButton';
import { Card } from '@/components/Card/card';
import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { useToast } from '@/components/ui/use-toast';
import { aggregateChunkResults, buildErrorRowsCsv, buildErrorRowsCsvFileName, canApply, ChunkResultPanel, currentPhaseKind, deriveImportStep, downloadTextFile, hasJobFailed, ImportProgress, ImportStepperHeader, isApplied, isImportConflictError, isImportNotFoundError, isJobSettled, useApplyImportSession, useChunkResults, useImportJobDetail } from '@/features/people-csv-import';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
const CsvImportDetail = () => {
  const {
    importId
  } = Route.useParams();
  const navigate = Route.useNavigate();
  const {
    toast
  } = useToast();
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.csvImport'
  });
  const {
    data: job,
    error,
    isPending,
    isError,
    refetch
  } = useImportJobDetail(importId);
  const {
    mutate: applySession,
    isPending: isApplying
  } = useApplyImportSession(importId);
  useEffect(() => {
    if (error != null && isImportNotFoundError(error)) {
      toast({
        description: t('errors.notFound'),
        variant: 'destructive'
      });
      navigate({
        to: '/person/csv-import',
        search: {}
      });
    }
  }, [error, navigate, toast, t]);
  const settled = job != null && isJobSettled(job);
  const failed = job != null && hasJobFailed(job);
  const currentStep = job != null ? deriveImportStep(job) : 1;

  // 表示対象 chunk（現在フェーズ = validation / apply）
  const relevantChunks = useMemo(() => {
    if (job == null) return [];
    const wantKind = currentPhaseKind(job);
    const filtered = job.chunks.filter(c => c.kind === wantKind);
    return filtered.length > 0 ? filtered : job.chunks;
  }, [job]);

  // パース自体の失敗（プレビューするセル結果が無い）
  const isParseLevelFailure = job?.failureReasonCode === 'csv_parse_failed' || job?.failureReasonCode === 'empty_csv_file';

  // 検証は完走したが有効行が 0（全行エラー）のケース
  const isAllInvalid = job != null && job.counts.validCount === 0 && (job.counts.invalidCount ?? 0) > 0;
  const failureMessage = job != null && failed ? match(job.failureReasonCode).with('csv_parse_failed', () => t('detail.failureReason.csvParseFailed')).with('empty_csv_file', () => t('detail.failureReason.emptyCsvFile')).otherwise(() => isAllInvalid ? t('detail.failureReason.noValidRows') : t('detail.failureReason.unknown')) : null;

  // chunk 結果が取得できるならプレビューを表示する（失敗時でも）。
  // ただしパース失敗はセル結果が無いため、失敗カードのみとする。
  const hasResults = job != null && settled && !isParseLevelFailure && relevantChunks.length > 0;
  const showApplyAction = job != null && canApply(job);
  const showCompleteAction = job != null && isApplied(job);

  // エラー行ダウンロード用に、表示対象の全 chunk 結果を集約する（ヘッダーのボタンから配線）。
  const chunkResultQueries = useChunkResults(importId, relevantChunks, hasResults);
  const errorRowsCsv = useMemo(() => {
    // 一部 chunk が未ロード/失敗の状態で生成すると不完全な CSV になるため、全件成功するまで null
    const allChunksLoaded = chunkResultQueries.length > 0 && chunkResultQueries.every(query => query.isSuccess);
    if (!allChunksLoaded) return null;
    const loadedResults = chunkResultQueries.map(query => query.data?.result).filter((result): result is NonNullable<typeof result> => result != null);
    const aggregated = aggregateChunkResults(loadedResults);
    return aggregated != null ? buildErrorRowsCsv(aggregated) : null;
  }, [chunkResultQueries]);
  const handleApply = () => {
    if (job == null) return;
    // 新しい importId は作らず、同じセッションに適用フェーズを開始する
    applySession(undefined, {
      onError: applyError => {
        toast({
          description: isImportConflictError(applyError) ? t('errors.conflict') : t('errors.applyFailed'),
          variant: 'destructive'
        });
      }
    });
  };
  const handleDownloadErrorRows = () => {
    if (job == null || errorRowsCsv == null) return;
    downloadTextFile(buildErrorRowsCsvFileName(job.originalFileName), errorRowsCsv);
  };

  // 初回ロード中は画面中央にローダーを表示する（loading-state.md の3分岐）
  if (isPending) {
    return <ViewportLoader />;
  }

  // 404 はトースト表示 + 一覧へのリダイレクトを上の useEffect が担うため、
  // 遷移が完了するまでの一瞬は何も描画しない
  if (error != null && isImportNotFoundError(error)) {
    return null;
  }

  // notFound 以外の取得失敗（または data 未確定）はエラー表示 + 再試行導線を出す
  if (isError || job == null) {
    return <VStack className="h-[calc(100vh-64px)] w-full items-center justify-center gap-4 p-10">
        <Card className="w-full max-w-xl border-error-border bg-error-muted-fill p-6 text-error-on-muted-fill">
          <VStack className="items-center gap-4">
            <HStack className="items-center gap-2">
              <Icon iconName="AlertCircle" colorVariant="error" />
              <Text variant="pBold">{t('detail.failureReason.unknown')}</Text>
            </HStack>
            <ActionButton variant="neutral" onClick={() => refetch()}>
              {t('detail.retryButton')}
            </ActionButton>
          </VStack>
        </Card>
      </VStack>;
  }
  return <VStack className="h-[calc(100vh-64px)] w-full items-stretch gap-0 overflow-hidden bg-neutral-bottom-fill">
      {/* ページヘッダー帯（白背景 + border-b）。ワークエリアのグレー地とは切り離す */}
      <VStack className="w-full shrink-0 items-start gap-3 border-b border-neutral-muted-border bg-neutral-fill px-8 pt-5 pb-[18px]">
        <Link to="/person/csv-import" search={{}}>
          <HStack className="items-center gap-1">
            <Text variant="labelReg" className="text-neutral-muted-on-fill">
              {t('list.title')}
            </Text>
            <Icon iconName="ChevronRight" size={14} className="text-neutral-muted-on-fill" />
          </HStack>
        </Link>

        <HStack className="w-full flex-wrap items-center justify-between gap-7">
          <HStack className="flex-wrap items-center gap-6">
            <Text variant="h3">{job.originalFileName}</Text>
            <ImportStepperHeader currentStep={currentStep} inline />
          </HStack>

          <HStack className="shrink-0 items-center gap-2.5">
            <ActionButton variant="neutral" className="h-10" icon="Download" disabled={errorRowsCsv == null} onClick={handleDownloadErrorRows}>
              {t('detail.downloadErrorRowsButton')}
            </ActionButton>
            {showApplyAction && <ActionButton variant="default" className="h-10" icon="PlayIcon" isLoading={isApplying} onClick={handleApply}>
                {t('detail.applyButton')}
              </ActionButton>}
            {showCompleteAction && <Link to="/people/workforce">
                <ActionButton variant="default" className="h-10">
                  {tc('action.complete')}
                </ActionButton>
              </Link>}
          </HStack>
        </HStack>
      </VStack>

      {/* バナー領域（進捗/失敗）。チャンクパネルとは独立して余白を持たせる */}
      {(failureMessage != null || !settled) && <VStack className="w-full shrink-0 items-start gap-4 px-6 pt-5">
          {failureMessage != null && <Card className="w-full shrink-0 border-error-border bg-error-muted-fill p-6 text-error-on-muted-fill">
              <HStack className="items-center gap-2">
                <Icon iconName="AlertCircle" colorVariant="error" />
                <Text variant="pBold">{failureMessage}</Text>
              </HStack>
            </Card>}

          {!settled && <ImportProgress counts={job.counts} phase={currentStep >= 3 ? 'apply' : 'validation'} />}
        </VStack>}

      {/* ワークエリア（チャンクパネルは白背景でページ端まで密着、コンテンツ側のみグレー地+padding） */}
      {hasResults && <ChunkResultPanel importId={importId} chunks={relevantChunks} />}
    </VStack>;
};
export const Route = createFileRoute('/_staff/person/csv-import/$importId')({
  component: CsvImportDetail
});